/**
 * Authenticated HTTP Client
 * Wraps resilientFetch with automatic Bearer token injection from Azure B2C
 *
 * Copied verbatim from user app utils/authenticatedFetch.ts.
 * The admin site is a separate app — copy, not import.
 */

import { resilientFetch, type RetryConfig, type CircuitBreakerConfig } from './resilientFetch';

/**
 * Token provider function type - returns access token or null
 */
export type TokenProvider = () => Promise<string | null>;

/**
 * Global token provider instance (set by useAuthenticatedFetch hook)
 */
let globalTokenProvider: TokenProvider | null = null;

/**
 * Set the global token provider function
 * Called by useAuthenticatedFetch hook on mount
 */
export function setTokenProvider(provider: TokenProvider | null): void {
  globalTokenProvider = provider;
}

/**
 * Get the current token provider
 */
export function getTokenProvider(): TokenProvider | null {
  return globalTokenProvider;
}

/**
 * Options for authenticated fetch
 */
export interface AuthenticatedFetchOptions {
  /** Skip authentication for this request */
  skipAuth?: boolean;
  /** Custom token provider (overrides global) */
  tokenProvider?: TokenProvider;
  /** Retry configuration */
  retryConfig?: Partial<RetryConfig>;
  /** Circuit breaker configuration */
  circuitBreakerConfig?: Partial<CircuitBreakerConfig>;
}

/**
 * Authenticated fetch with automatic Bearer token injection
 * Uses resilientFetch under the hood for retry and circuit breaker support
 */
/**
 * Deployed builds must dial the API host absolutely — the SWA serves no API,
 * so a relative /api path gets index.html back ("<!doctype ... is not valid
 * JSON" errors on every data page). Local dev keeps relative paths (Vite proxy).
 */
const API_BASE = ((import.meta.env.VITE_API_URL as string | undefined) ?? '').replace(/\/$/, '');

function resolveApiUrl(input: RequestInfo | URL): RequestInfo | URL {
  if (API_BASE && typeof input === 'string' && input.startsWith('/')) {
    return `${API_BASE}${input}`;
  }
  return input;
}

export async function authenticatedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  options: AuthenticatedFetchOptions = {}
): Promise<Response> {
  const { skipAuth = false, tokenProvider, retryConfig, circuitBreakerConfig } = options;

  // Determine which token provider to use
  const provider = tokenProvider || globalTokenProvider;

  // Build headers with authorization if not skipped
  const headers = new Headers(init?.headers);

  if (!skipAuth && provider) {
    try {
      const token = await provider();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
    } catch (error) {
      console.warn('[AuthenticatedFetch] Failed to acquire token:', error);
      // Continue without auth - let the API return 401 if needed
    }
  }

  // Merge headers into init
  const authenticatedInit: RequestInit = {
    ...init,
    headers,
  };

  // Use resilient fetch with auth headers
  const response = await resilientFetch(resolveApiUrl(input), authenticatedInit, retryConfig, circuitBreakerConfig);

  // Handle 401 Unauthorized - token might be expired
  if (response.status === 401 && provider && !skipAuth) {
    console.warn('[AuthenticatedFetch] Received 401 - token may be expired');
  }

  return response;
}

/**
 * Create an authenticated fetch function with preset options
 */
export function createAuthenticatedFetch(
  defaultOptions: AuthenticatedFetchOptions = {}
): (input: RequestInfo | URL, init?: RequestInit, options?: AuthenticatedFetchOptions) => Promise<Response> {
  return (input, init, options) =>
    authenticatedFetch(input, init, { ...defaultOptions, ...options });
}

export default authenticatedFetch;
